import { useEffect, useMemo, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CompanyExpenseSummary } from '../../types/company-expense';
import { formatMoney } from '../../utils/format';

const sortGetters: Record<
  string,
  (
    row: CompanyExpenseSummary['categories'][number],
  ) => string | number | boolean | null | undefined
> = {
  categoryName: (r) => r.categoryName,
  count: (r) => r.count,
  net: (r) => r.net,
  vat: (r) => r.vat,
  gross: (r) => r.gross,
  invoice: (r) => r.invoice,
  cash: (r) => r.cash,
};

export function CompanyExpenseSummaryPage() {
  const labels = t().companyExpenses;
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [report, setReport] = useState<CompanyExpenseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('categoryName');

  const displayRows = useMemo(
    () =>
      report ? sortItems(report.categories, sort, sortGetters) : [],
    [report, sort],
  );

  const years = useMemo(() => {
    const current = now.getFullYear();
    return [current - 1, current, current + 1];
  }, [now]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCompanyExpenseSummary({
          month: Number(month),
          year: Number(year),
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  return (
    <PageShell>
      <ListFiltersBar>
        <FilterField label={labels.month}>
          <Select
            value={month}
            size="sm"
            options={t().common.months.map((name, index) => ({
              value: index + 1,
              label: name,
            }))}
            onChange={(e) => setMonth(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.year}>
          <Select
            value={year}
            size="sm"
            options={years.map((y) => ({ value: y, label: String(y) }))}
            onChange={(e) => setYear(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : !report || displayRows.length === 0 ? (
          <ListEmptyState title={labels.emptyTitle} hint={labels.emptyHint} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <SortableTh
                  sortKey="categoryName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.category}
                </SortableTh>
                <SortableTh
                  sortKey="count"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.count}
                </SortableTh>
                <SortableTh
                  sortKey="net"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.amount}
                </SortableTh>
                <SortableTh
                  sortKey="vat"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.vatAmount}
                </SortableTh>
                <SortableTh
                  sortKey="gross"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {labels.gross}
                </SortableTh>
                <SortableTh
                  sortKey="invoice"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {t().financial.invoice}
                </SortableTh>
                <SortableTh
                  sortKey="cash"
                  sort={sort}
                  onSort={toggleSort}
                  className="text-right"
                >
                  {t().financial.cash}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => (
                <tr key={row.categoryId} className="border-t border-line">
                  <td className="px-3 py-2 font-medium">{row.categoryName}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.count}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.net)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.vat)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.gross)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.invoice)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.cash)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-line bg-surface-muted/40 font-semibold">
                <td className="px-3 py-2">{t().common.total}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {report.totals.count}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.net)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.vat)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.gross)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.invoice)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.cash)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
